import React, { useEffect } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from './context/AuthContext';
import IntroPage from './pages/IntroPage/IntroPage';
import CoursePage from './pages/CoursePage/CoursePage';
import Home from './pages/Home/Home';
import Signup from './components/Auth/Signup';
import Login from './components/Auth/Login';
import EditProfile from './pages/EditProfile/EditProfile';
import AccountSettings from './pages/AccountSettings/AccountSettings';
import Wishlist from './pages/Wishlist/Wishlist';
import Subscriptions from './pages/Subscriptions/Subscriptions';
import Cart from './pages/Cart/Cart';
import ForgotPassword from './components/Auth/ForgotPassword';
import Settings from './pages/Settings/Settings';
import AddNewCourse from './pages/AddNewCourse/AddNewCourse';
import MyCourses from './pages/MyCourses/MyCourses';
import Profile from './pages/Profile/Profile';
import Dashboard from './components/Dashboard/Dashboard';
import CourseDetails from './pages/CourseDetails/CourseDetails';
import MyLearning from './pages/MyLearning/MyLearning';
import CourseSuggestion from './components/CourseSuggestion/CourseSuggestion';
import InstructorCourseDetailes from './pages/InstructorCourseDetailes/InstructorCourseDetailes';
import UserTopbar from './pages/UsersTopbar/UsersTopbar';
import MessagesPage from './components/MessagesPage/MessagesPage';
import SearchResults from './pages/SearchResults/SearchResults';
import CategoryCourses from './pages/CategoryCourses/CategoryCourses';
import CheckoutPage from './components/CheckoutPage/CheckoutPage';
import InstructorCriteria from './pages/InstructorCriteria/InstructorCriteria';
import InstructorLandingPage from './pages/InstructorLandingPage/InstructorLandingPage';
import RoleSelection from './pages/RoleSelection/RoleSelection';
import InstructorToolsPage from './components/InstructorToolsPage/InstructorToolsPage';
import InstructorFeedback from './pages/InstructorFeedback/InstructorFeedback';
import UsersFeedback from './pages/UsersFeedback/UsersFeedback';
import InstructorNotificationPage from './pages/InstructorNotification/InstructorNotification';
import Performance from './pages/Performance/Performance';
import BlankPage from './components/BlankPage/BlankPage';
import ContactAdminPage from './components/ContactAdminPage/ContactAdminPage';
import CourseOverview from './components/CourseOverview/CourseOverview';
import InstructorPayout from './pages/InstructorPayout/InstructorPayout';
import TermsAndConditions from './components/TermsAndConditions/TermsAndConditions';
import PrivacyPolicy from './components/PrivacyPolicy/PrivacyPolicy';
import TotalStudents from './pages/TotalStudents/TotalStudents';
import FAQs from './pages/FAQs/FAQs';
import InstructorProfile from './pages/InstructorProfile/InstructorProfile';
import CertificateGenerator from './components/CertificateGenerator/CertificateGenerator';
import MyCertificate from './pages/MyCertificate/MyCertificate';
import CertificatesId from './pages/Certificates/Certificates';
import InstructorAbout from './pages/InstructorAbout/InstructorAbout';
import GiftedPage from './pages/GiftedPage/GiftedPage';
import AboutUs from './pages/AboutUs/AboutUs';
import UserCritiria from './pages/UserCritiria/UserCritiria';
import UserNotifications from './pages/UserNotifications/UserNotifications';
import InstructorGuide from './pages/InstructorGuide/InstructorGuide';
import RespondDeletionRequest from './pages/RespondDeletionRequest/RespondDeletionRequest';
import UniversityLanding from './pages/UniversityLanding/UniversityLanding';
import UniversityCriteria from './pages/UniversityLanding/UniversityCriteria';
import UniversityHome from './pages/UniversityLanding/UniversityHome';
import UniversityCourses from './pages/UniversityLanding/UniversityCourses';
import UniversitySettings from './pages/UniversityLanding/UniversitySettings';
import { NotificationProvider } from './context/NotificationContext';
import { SocketProvider } from './context/SocketContext';
import { CartProvider } from './context/CartContext';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { initializeErrorHandling } from './utils/errorHandler';
import PWAInstallPrompt from './components/PWA/PWAInstallPrompt';

const App = () => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize error handling for browser extensions
    initializeErrorHandling();

    if (isLoggedIn && window.location.pathname === '/') {
      navigate('/home');
    }
  }, [isLoggedIn, navigate]);

  
  
  return (
    <ErrorBoundary>
      <SocketProvider>
        <NotificationProvider>
          <CartProvider>
            <Routes>
              {isLoggedIn && (
                <>
                  <Route path="/home" element={<Home />} />
                  <Route path="/edit-profile" element={<EditProfile />} />
                  <Route path="/account-settings" element={<AccountSettings />} />
                  <Route path="/instructor-guide" element={<InstructorGuide />} />
                  <Route path="/university" element={<UniversityLanding />} />
                  <Route path="/university-criteria" element={<UniversityCriteria />} />
                  <Route path="/university-home" element={<UniversityHome />} />
                  <Route path="/instructor-payout" element={<InstructorPayout />} />
                  <Route path="/users-feedback" element={<UsersFeedback />} />
                  <Route path="/instructor-feedback" element={<InstructorFeedback />} />
                  <Route path="/subscriptions" element={<Subscriptions />} />
                  <Route path="/wishlist" element={<Wishlist />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/learning" element={<MyLearning />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/my-courses" element={<MyCourses />} />
                  <Route path="/add-new-course" element={<AddNewCourse />} />
                  <Route path="/performance" element={<Performance />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/courses/:id" element={<CourseDetails />} />
                  <Route path="/course-details/:id" element={<CourseDetails />} />
                  <Route path="/suggestion" element={<CourseSuggestion />} />
                  <Route path="/instructor-course-details/:id" element={<InstructorCourseDetailes />} />
                  <Route path="/UsersTopbar" element={<UserTopbar />} />
                  <Route path="/message" element={<MessagesPage />} />
                  <Route path="/notifications" element={<UserNotifications />} />
                  <Route path="/search" element={<SearchResults />} />
                  <Route path="/category/:categoryName/:subcategoryName" element={<CategoryCourses />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/instructor-criteria" element={<InstructorCriteria />} />
                  <Route path="/instructor-landing" element={<InstructorLandingPage />} />
                  <Route path="/role-selection" element={<RoleSelection />} />
                  <Route path="/tools" element={<InstructorToolsPage />} />
                  <Route path="/Instructor-notifications" element={<InstructorNotificationPage />} />
                  <Route path="/blank-page" element={<BlankPage />} />
                  <Route path="/contact-admin" element={<ContactAdminPage />} />
                  <Route path="/course/:courseId" element={<CourseOverview />} />
                  <Route path="/total-students" element={<TotalStudents />} />
                  <Route path="/faq" element={<FAQs />} />
                  <Route path="/faqs" element={<FAQs />} />
                  <Route path="/instructor/:id" element={<InstructorProfile />} />
                  <Route path="/certificate/:courseId" element={<CertificateGenerator />} />
                  <Route path="/my-certificates" element={<MyCertificate />} />
                  <Route path="/instructor-about" element={<InstructorAbout />} />
                  <Route path="/gifted" element={<GiftedPage />} />
                  <Route path="/about" element={<AboutUs />} />
                  <Route path="/university-courses" element={<UniversityCourses />} />
                  <Route path="/university-settings" element={<UniversitySettings />} />
                </>
              )}

              <Route path="/" element={isLoggedIn ? <Home /> : <IntroPage />} />

              <Route path="/signup" element={<Signup />} />
              <Route path="/login" element={<Login />} />
              {/* Public route for instructor deletion response (handles its own login check) */}
              <Route path="/instructor/respond-deletion/:id" element={<RespondDeletionRequest />} />
              <Route path="/courses/:subcategory" element={<CoursePage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/publicCertificateView/:certificateId" element={<CertificatesId />} />
              <Route path="/user-criteria" element={<UserCritiria />} />
            </Routes>

            <PWAInstallPrompt />
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
          </CartProvider>
        </NotificationProvider>
      </SocketProvider>
    </ErrorBoundary>
  );
};

export default App;
